import React, { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/site-data";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F8FAF8]/85 backdrop-blur-xl border-b border-[#D1DED5]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <button
          data-testid="navbar-logo"
          onClick={() => handleNavClick("hero")}
          className="flex items-center gap-3 group"
          aria-label="Imperial Landscaping home"
        >
          <BrandMark className="w-11 h-11 drop-shadow-[0_8px_18px_rgba(46,92,62,0.45)]" dotRing="#F8FAF8" />
          <div className="hidden sm:flex flex-col items-start leading-none">
            <span className="font-['Outfit'] font-black text-lg tracking-tight text-[#1A2E20]">
              Imperial
            </span>
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#4A6E55] font-semibold">
              Landscaping, TLH
            </span>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleNavClick(l.id)}
              className="link-draw text-sm font-semibold tracking-wide text-[#1A2E20] hover:text-[#2E5C3E] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="navbar-call-link"
            href={`tel:${SITE.phoneTel}`}
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#1A2E20] hover:text-[#2E5C3E] transition-colors px-3 py-2"
          >
            <Phone className="w-4 h-4" />
            {SITE.phone}
          </a>
          <Button
            data-testid="navbar-quote-btn"
            onClick={() => handleNavClick("contact")}
            className="hidden md:inline-flex bg-[#EAB308] text-[#422006] hover:bg-[#FACC15] font-bold rounded-full px-5 h-11 shadow-[0_8px_22px_-10px_rgba(234,179,8,0.85)] transition-transform hover:-translate-y-0.5"
          >
            Free Quote
          </Button>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl border border-[#D1DED5] bg-white text-[#1A2E20]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-panel"
          className="md:hidden border-t border-[#D1DED5] bg-[#F8FAF8]/95 backdrop-blur-xl"
        >
          <div className="px-4 sm:px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => handleNavClick(l.id)}
                className="text-left text-base font-semibold text-[#1A2E20] hover:text-[#2E5C3E]"
              >
                {l.label}
              </button>
            ))}
            <a
              href={`tel:${SITE.phoneTel}`}
              className="inline-flex items-center gap-2 text-base font-semibold text-[#2E5C3E]"
            >
              <Phone className="w-4 h-4" />
              {SITE.phone}
            </a>
            <Button
              data-testid="navbar-mobile-quote-btn"
              onClick={() => handleNavClick("contact")}
              className="bg-[#EAB308] text-[#422006] hover:bg-[#FACC15] font-bold rounded-full h-12"
            >
              Get a Free Quote
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
